import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

const DataCatalog: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#003366', color: 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/')}
            sx={{ 
              color: 'white', 
              mb: 2,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Home
          </Button>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 700, mb: 2, color: 'white' }}>
            Federal Data Discovery
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.1rem' }, color: 'white' }}>
            FISMA-Compliant Data Catalog for Government Agencies
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '0.95rem', mb: 3, maxWidth: '800px', lineHeight: 1.6 }}>
            Comprehensive metadata management with automated security classification, cross-agency data sharing capabilities, and full compliance with federal data standards.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: '#003366',
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/login')}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Sign In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Key Features */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
          Key Features
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              icon: <SearchIcon sx={{ fontSize: 40 }} />,
              title: 'Advanced Search & Discovery',
              desc: 'Powerful search capabilities across all data assets with natural language processing and AI-powered recommendations'
            },
            {
              icon: <SecurityIcon sx={{ fontSize: 40 }} />,
              title: 'Automated Classification',
              desc: 'Automatic security classification based on FISMA requirements and federal data sensitivity guidelines'
            },
            {
              icon: <StorageIcon sx={{ fontSize: 40 }} />,
              title: 'Metadata Management',
              desc: 'Comprehensive metadata capture, lineage tracking, and data quality scoring for all assets'
            },
            {
              icon: <CheckIcon sx={{ fontSize: 40 }} />,
              title: 'Cross-Agency Sharing',
              desc: 'Secure data sharing protocols compliant with federal interagency data sharing agreements'
            }
          ].map((feature, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Card sx={{ height: '100%', p: 3 }}>
                <Box sx={{ color: '#003366', mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700, color: '#003366', mb: 4, fontSize: { xs: '1.5rem', md: '2rem' } }}>
            Benefits
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  For Data Stewards
                </Typography>
                <List>
                  {[
                    'Centralized metadata repository',
                    'Automated data quality monitoring',
                    'Lineage tracking and impact analysis',
                    'Compliance reporting dashboards'
                  ].map((item, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <CheckIcon sx={{ color: '#003366' }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  For Data Consumers
                </Typography>
                <List>
                  {[
                    'Self-service data discovery',
                    'Clear data ownership and contacts',
                    'Access request workflows',
                    'Data quality indicators'
                  ].map((item, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <CheckIcon sx={{ color: '#003366' }} />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Card sx={{ p: 4, textAlign: 'center', bgcolor: '#e3f2fd' }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#003366', fontWeight: 700, fontSize: { xs: '1.35rem', md: '1.75rem' } }}>
            Ready to Transform Your Data Discovery?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            Join federal agencies already using E-Unify for their data catalog needs
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              bgcolor: '#003366',
              color: 'white',
              px: 6,
              py: 2,
              '&:hover': { bgcolor: '#002244' }
            }}
          >
            Get Started Today
          </Button>
        </Card>
      </Container>
    </Box>
  );
};

export default DataCatalog;
